import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from 'features/common'
import { getFirebaseAppGallery } from 'features/common/api/firebase'
import { User } from 'features/users'
import { collection, getDocs, getFirestore, query } from 'firebase/firestore'
import settings from 'settings'
import { App, AppPayload, AppTypes, FeaturedApp, PatchAppParams } from '../types'

function baseListedAppsUrl() {
  const { workspaceApiUrl } = settings
  return `${workspaceApiUrl}/apps/listed`
}

function basePrivateAppsUrl() {
  const { accountsApiUrl } = settings
  return `${accountsApiUrl}/apps/private`
}

function baseUrlFor(type: AppTypes) {
  const baseUrl = type === AppTypes.Private ? basePrivateAppsUrl() : baseListedAppsUrl()
  return baseUrl
}

export const getApps = async ({ signal }: { signal?: AbortSignal }): Promise<Array<App>> => {
  const listedApps = await getRequest(baseListedAppsUrl(), { signal })
  return [...listedApps.map((app: App) => ({ ...app, type: 'listed' }))]
}

export const getApp = async (id: string): Promise<App> => {
  const baseUrl = baseListedAppsUrl()
  const app = await getRequest(`${baseUrl}/${id}`)
  return { ...app, type: AppTypes.Listed }
}

export const saveApp = async (data: App): Promise<App> => {
  const { id, type, ...payload } = data

  const baseUrl = baseUrlFor(type)
  if (id) {
    return await putRequest(`${baseUrl}/${id}`, { ...cleanUpAppPayload(payload) })
  } else {
    return await postRequest(`${baseUrl}`, { ...cleanUpAppPayload(payload) })
  }
}

export const deleteApp = async ({ id, type }: { id: string; type: AppTypes }): Promise<void> => {
  const baseUrl = baseUrlFor(type)
  await deleteRequest(`${baseUrl}/${id}`)
}

export const trackAppSelected = async ({
  user,
  appId,
  type
}: {
  user: User
  appId: string
  type?: string
}): Promise<void> => {
  const { workspaceApiUrl } = settings
  const eventType = type ? type : 'AppSelected'

  const metricsEndpointUrl = `${workspaceApiUrl}/events`
  postRequest(metricsEndpointUrl, {
    event_type: eventType,
    payload: {
      accountId: user.user_metadata.account_id,
      userId: user.user_id,
      appId: appId
    },
    timestamp: new Date()
  })
}

const cleanUpAppPayload = (app: AppPayload) => {
  return {
    allowed_logout_urls: app.allowed_logout_urls,
    allowed_origins: app.allowed_origins,
    app_type: app.app_type,
    callbacks: app.callbacks,
    description: app.description,
    initiate_login_uri: app.initiate_login_uri,
    title: app.title,
    url: app.url,
    web_origins: app.web_origins,
    token_endpoint_auth_method: app.token_endpoint_auth_method,
    bookmarked: app.bookmarked,
    privacy: app.privacy,
    thumbnail_url: app.thumbnail_url,
    groups: app.groups?.map((g) => (typeof g === 'string' ? g : g.id)),
    sharingScope: app.sharingScope
  }
}

export const getBookmarkedApps = async (): Promise<Array<App>> => {
  const params = '?bookmarked=true'
  const apps = await getRequest(`${baseListedAppsUrl()}${params}`)
  return apps
}

export const getFeaturedApps = async (): Promise<Array<FeaturedApp>> => {
  const firebaseApp = getFirebaseAppGallery()
  const COLLECTION_NAME = 'app_gallery'
  const db = getFirestore(firebaseApp)
  const q = query(collection(db, COLLECTION_NAME))
  const querySnapshot = await getDocs(q)

  const featuredApps: FeaturedApp[] = []
  querySnapshot.forEach((doc) => {
    const docData = doc.data()
    if (docData.show) {
      featuredApps.push({
        id: docData.id,
        title: docData.title,
        order: docData.priority,
        description: docData.description,
        meta: { ...docData.meta },
        main_content: { ...docData.main_content },
        media: docData.media
      })
    }
  })
  return featuredApps.sort((a, b) => ((a?.order || 1) > (b?.order || 0) ? 1 : -1))
}

export const patchApp = async (appEdited: PatchAppParams): Promise<App> => {
  const app = await patchRequest(`${baseListedAppsUrl()}/${appEdited.id}`, {
    groups: appEdited.groups?.map((group) => (typeof group === 'string' ? group : group.id)) ?? [],
    privacy: appEdited.privacy,
    sharingScope: appEdited.sharingScope
  })
  return app
}
